import { UUID } from 'angular2-uuid';
import { StorageBase } from 'src/app/storage/models/storage-base';
import { IRepository } from '../repository/repository.interface';
import { Observable, of, concatMap, catchError, throwError, shareReplay, map } from 'rxjs';
import { DuplicateKeyError } from '../repository/duplicate-key.error';
import { RecordNotFoundError } from '../repository/record-not-found.error';
import { RaceTolerantInsertOutcome } from './race-tolerant-insert-outcome.enum';

/**
 * Abstract base class that provides in-memory caching functionality for repositories.
 * Manages both individual item caching and full dataset caching.
 *
 * @template T The type of entity being cached
 */
export abstract class CacheManager<T extends StorageBase> implements IRepository<T> {

	/** A map to store cached items. */
	private readonly cachedItems: Map<UUID, T> = new Map<UUID, T>();

	/** Indicates whether all items have been loaded into the cache. */
	private isAllCachedItemsLoaded: boolean = false;

	/** Observable for the getAll operation to prevent multiple simultaneous requests from the UI before the first one completes. */
	private currentGetAllOperation$: Observable<T[]> | null = null;

	/**
	 * @param repository The underlying repository to interact with the data source.
	 */
	constructor(
		private readonly repository: IRepository<T>
	) {}

	/** @inheritdoc */
	public GetAll$(): Observable<T[]> {
		// If currently loading, return the existing operation to prevent excessive calls to indexedDB
		if (this.currentGetAllOperation$) {
			return this.currentGetAllOperation$;
		}

		const allCachedItems: T[] | null = this.GetAllCachedItems();
		if (allCachedItems) {
			return of(allCachedItems);
		}

		this.currentGetAllOperation$ = this.repository.GetAll$().pipe(
			concatMap((items: T[]) => {
				this.SetAllCachedItems(items);
				this.currentGetAllOperation$ = null;
				return of(items);
			}),
			catchError((error: any) => {
				this.currentGetAllOperation$ = null;
				return throwError(() => error);
			}),
			shareReplay({bufferSize: 1, refCount: true})
		);

		return this.currentGetAllOperation$;
	}

	/** @inheritdoc */
	public GetById$(id: UUID): Observable<T | null> {
		const cachedItem: T | null = this.GetCachedItem(id);
		if (cachedItem) {
			return of(cachedItem);
		}
		return this.repository.GetById$(id).pipe(
			concatMap((item: T | null) => {
				if (item) {
					this.AddCachedItem(item);
				}
				return of(item);
			}),
		);
	}

	/** @inheritdoc */
	public GetByIndex$(searchCriteria: Record<string, UUID | string | number>): Observable<T[]> {
		const cachedItems: T[] | null = this.GetAllCachedItems();
		if (cachedItems) {
			return of(cachedItems.filter((item: T) => {
				return Object.entries(searchCriteria).every(([key, value]) => {
					return (item as Record<string, any>)[key] === value;
				});
			}));
		}

		return this.repository.GetByIndex$(searchCriteria).pipe(
			concatMap((items: T[]) => {
				items.forEach((item: T) => this.AddCachedItem(item));
				return of(items);
			})
		);
	}

	/**
	 * Inserts an item, tolerating the race where a concurrent caller inserts the same item first.
	 * On failure, re-reads to distinguish a lost race (returns AlreadyPresent) from a true storage failure (rethrows).
	 * Callers are responsible for logging domain events based on the outcome.
	 * @param item The item to insert.
	 * @returns Observable emitting the outcome of the insert attempt.
	 */
	public InsertToleratingRace$(item: T): Observable<RaceTolerantInsertOutcome> {
		return this.Insert$(item).pipe(
			map(() => RaceTolerantInsertOutcome.Inserted),
			catchError(() => this.GetById$(item.Id).pipe(
				map((existing: T | null) => {
					if (existing !== null) {
						return RaceTolerantInsertOutcome.AlreadyPresent;
					}
					throw new Error(`Insert failed and the record is still absent for id ${item.Id}.`);
				})
			))
		);
	}

	/** @inheritdoc */
	public Insert$(service: T): Observable<UUID> {
		this.AddCachedItem(service);

		return this.repository.Insert$(service).pipe(
			catchError((error: Error) => {
				this.RemoveCachedItem(service.Id);
				if (error instanceof DuplicateKeyError) {
					return throwError(() => error);
				}
				return throwError(() => new Error('Insert failed and item was removed from cache.'));
			})
		);
	}

	/** @inheritdoc */
	public Update$(service: T): Observable<void> {
		this.AddCachedItem(service);

		return this.repository.Update$(service).pipe(
			catchError((error: Error) => {
				this.isAllCachedItemsLoaded = false;
				if (error instanceof RecordNotFoundError) {
					return throwError(() => error);
				}
				return throwError(() => new Error('Update failed and item was removed from cache.'));
			})
		);
	}

	/** @inheritdoc */
	public DeleteById$(id: UUID): Observable<void> {
		this.RemoveCachedItem(id);

		return this.repository.DeleteById$(id).pipe(
			catchError(() => {
				this.isAllCachedItemsLoaded = false;
				return throwError(() => new Error('Delete failed and item was removed from cache.'));
			})
		);
	}

	/**
	 * Sets all items to cache.
	 * If the items are already cached, they will be replaced.
	 * @param items The complete dataset to cache
	 */
	private SetAllCachedItems(items: T[]): void {
		this.cachedItems.clear();
		items.forEach((item: T) => this.AddCachedItem(item));
		this.isAllCachedItemsLoaded = true;
	}

	/**
	 * Gets all cached items.
	 * @returns Cached items array or null if not available
	 */
	private GetAllCachedItems(): T[] | null {
		if (this.isAllCachedItemsLoaded) {
			return [...this.cachedItems.values()];
		}
		return null;
	}

	/**
	 * Adds an item to cache.
	 * If the item already exists, it will be updated.
	 * If it does not exist, it will be added.
	 * @param item The item to add to cache
	 */
	private AddCachedItem(item: T): void {
		this.cachedItems.set(item.Id, item);
	}

	/**
	 * Gets an item from cache if available.
	 * @param id The ID of the item to retrieve
	 * @returns The cached item or null if not found
	 */
	private GetCachedItem(id: UUID): T | null {
		return this.cachedItems.get(id) ?? null;
	}

	/**
	 * Removes an item from cache.
	 * @param id The ID of the item to remove
	 */
	private RemoveCachedItem(id: UUID): void {
		this.cachedItems.delete(id);
	}
}
